import React, { useMemo, useEffect } from 'react';
import { useSelector, getLocale } from 'umi';
import Filter from '../../components/ConsoleStoreRankMoreFilter';
import ExportTable from '@intelligence/components/ExportTable';
import StoreRankMoreCore from '../../../components/StoreRankMoreCore';
import useStoreTopRegionOptionsMap from '@intelligence/v3/_hooks/useStoreTopRegionOptionsMap';
import useFilterExtend from '@/pages/intelligence/hooks/useFilterExtend';
import useInitialCheckAllCountries from '@intelligence/pages/StoreRanks/components/StoreRankMoreCore/hooks/useInitialCheckAllCountries';
import useInitialTopChartsName from '@/pages/intelligence/pages/StoreRanks/components/StoreRankMoreCore/hooks/useInitialTopChartsName';
import useBuriedPointLogCfgKeys from '@/pages/intelligence/pages/StoreRanks/components/StoreRankMoreCore/hooks/useBuriedPointLogCfgKeys';
import useStoreRankMoreCore from '@/pages/intelligence/pages/StoreRanks/components/StoreRankMoreCore/hooks/useStoreRankMoreCore';
import { getAllCountries } from '@intelligence/utils/regionSelectUtils';
import { handleRangeDate2SingleDateRangeTimeFilterExtend } from '@intelligence/utils/dateUtils';
import { getIsMonthlyRank, getInitialDate, getInitialRegions } from '../../utils';
import { FILTER_EXTEND_NAMESPACE } from '@/pages/intelligence/models/filterExtend';
import * as FILTER_KEYS from '@/pages/intelligence/components/Filter/FilterBase/_config';
import { analysisPlatformTopInfo } from '@/pages/intelligence/_config/storeRank/utils';
import {
  PRIMARY_KEY,
  CONSOLE_UNIFIED_KEY,
  CONSOLE_SOURCES,
  PC_CONSOLE_SECTIONAL_RANK_KEYS,
  PC_CONSOLE_SECTIONAL_RANK_INFOS,
  CONSOLE_SOURCES_METRICS_KEYS,
  CONSOLE_PLATFORM_TOP_INFOS,
  CONSOLE_SOURCE_PLAYSTATION_BEST_SELLING,
  CONSOLE_SOURCES_PLAYSTATION_DAILY_MONTHLY_RANK,
  CONSOLE_SOURCES_PLAYSTATION_MONTHLY_RANK,
  CONSOLE_PLATFORM_NINTENDO,
  CONSOLE_PLATFORM_PLAYSTATION,
} from '@/pages/intelligence/_config/storeRank/config';
import { LANGUAGE_CN } from '@intelligence/_config/config';
import MarqueeBanner from '@intelligence/components/MarqueeBanner/index';
import {
  nitendoCountryCodes,
  nitendoUSCountryCodes,
} from '@/pages/intelligence/_config/storeRank/console';
import { isPlatformSupported } from '@intelligence/v3/_config/platformStoreTops';
import { CONSOLE_ENTITY_TYPE, CONSOLE_PLATFORMS } from '@intelligence/v3/constants/topcharts';

import type { IBaseProps, IDateType } from '@/pages/intelligence/types/common';
import type { IStoreRankMoreCoreFetchParams } from '@/pages/intelligence/types/storeRank';

const SEARCH_TYPE = 'console-store-rank-more';
const TOP_CHARTS_TYPE = 'store-rank-more';
const BUSINESS_TYPE = `${CONSOLE_ENTITY_TYPE}-${TOP_CHARTS_TYPE}`;
const DEFAULT_FETCH_PARAMS: IStoreRankMoreCoreFetchParams = {
  entity_type: CONSOLE_ENTITY_TYPE,
  topchart_name: CONSOLE_SOURCE_PLAYSTATION_BEST_SELLING,
  region_type: 'market',
  regions: ['us'],
  genre: [],
  sub_genre: [],
  genre_source: 'iegg',
  iegg_genre_req: [],
  granularity: 'daily',
  start_date: '',
  end_date: '',
  start_release_time: '',
  end_release_time: '',
  order_source: '',
  order_metric: 'rank',
  order: 'desc',
  ratio_type: 0,
  page: 1,
  page_size: 100,
  search_ids: [],
  search_by_name: '',
};

type IConsoleStoreRankMoreProps = IBaseProps;

const ConsoleStoreRankMore: React.FC<IConsoleStoreRankMoreProps> = (props) => {
  const {} = props;
  const locale = getLocale();
  const filterState = useSelector((rootState: any) => rootState?.[FILTER_EXTEND_NAMESPACE]) ?? {};
  const isOperate = !!filterState?.isOperate;

  const { filterExtend, setFilterExtend } = useFilterExtend({});

  // 是否初始化时勾选全部国家（从商店榜单页 点击下拉国家筛选器自定义footer按钮跳转过来的时候，固定勾选全部国家）
  const initialCheckAllCountries = useInitialCheckAllCountries();
  const initialTopChartsName = useInitialTopChartsName(CONSOLE_SOURCES);
  const { platform: initialPlatform } = analysisPlatformTopInfo(initialTopChartsName);

  const platforms = useMemo<IPlatform[]>(() => {
    if (isPlatformSupported(initialPlatform as IPlatform)) {
      return [initialPlatform] as IPlatform[];
    }

    return CONSOLE_PLATFORMS;
  }, [initialPlatform]);

  const { isLoading, regionsOptions, regionsOptionsMap } = useStoreTopRegionOptionsMap(initialPlatform, {
    entityType: CONSOLE_ENTITY_TYPE,
    platforms,
  });

  const initialFetchParams = useMemo<IStoreRankMoreCoreFetchParams>(() => {
    const defaultFetchParams = {
      ...DEFAULT_FETCH_PARAMS,
      topchart_name: initialTopChartsName,
    };
    if (!isOperate) return defaultFetchParams;

    const filterExtendTmp = handleRangeDate2SingleDateRangeTimeFilterExtend(filterExtend);
    const timeExtend = filterExtendTmp?.[FILTER_KEYS.RANGE_TIME];
    const composedCountryExtend = filterExtendTmp?.[FILTER_KEYS.COMPOSED_COUNTRY_SELECT];
    const singleAreaExtend = filterExtendTmp?.[FILTER_KEYS.SINGLE_AREA_SELECT];
    const initialGranularity = timeExtend?.granularity || DEFAULT_FETCH_PARAMS.granularity;

    const initialIsMonthlyRank = getIsMonthlyRank(initialPlatform, initialGranularity);

    const initialDate = getInitialDate(initialIsMonthlyRank, {
      extendStartDate: timeExtend?.start_date,
      extendEndDate: timeExtend?.end_date,
      defaultFilter: DEFAULT_FETCH_PARAMS,
    });
    const countriesValue = composedCountryExtend?.value?.countries;
    const isNintendoSingleRegion =
      initialPlatform === CONSOLE_PLATFORM_NINTENDO &&
      composedCountryExtend?.value?.type !== 'multiple';
    const nintendoUsOnly =
      initialPlatform === CONSOLE_PLATFORM_NINTENDO &&
      initialTopChartsName !== 'nintendo best sellers';

    const nitendoCode = nintendoUsOnly ? nitendoUSCountryCodes : nitendoCountryCodes;
    const nitendoCountries =
      countriesValue?.length === 1 && nitendoCode?.includes(countriesValue[0])
        ? countriesValue
        : ['us'];
    const initialRegions = getInitialRegions(initialIsMonthlyRank, {
      composedCountryExtend:
        isNintendoSingleRegion
          ? { countries: nitendoCountries }
          : composedCountryExtend?.value,
      singleAreaExtend: singleAreaExtend?.value,
      defaultFilter: DEFAULT_FETCH_PARAMS,
    });
    /** nintendo时修改继承值为过滤后的值 */
    if (isNintendoSingleRegion) {
      const newfilterExtend = {
        ...filterExtend,
        [FILTER_KEYS.COMPOSED_COUNTRY_SELECT]: {
          value: { type: 'single', countries: nitendoCountries },
        },
      };
      setFilterExtend(newfilterExtend);
    }
    return {
      ...defaultFetchParams,
      regions: initialRegions,
      granularity: initialGranularity,
      start_date: initialDate,
      end_date: initialDate,
    };
  }, [isOperate, initialTopChartsName]);

  const { pageLogCfg, downloadExcelLogCfgKey } = useBuriedPointLogCfgKeys(CONSOLE_ENTITY_TYPE, initialTopChartsName);

  const {
    fetchParams,
    isSectional,
    platform,
    platformName,
    topName,

    handleInit,
    handleConfirm,
    handleReset,

    isVisualisLoading,
    isVisualisNoData,
    seriesData,
    xAxisData,
    sortKeyName,
    currentLine,
    segments,
    segment,
    setSegment,
    yAxisFormatter,
    tooltipFormatter,
    createLegendsCustomMade,

    isTableLoading,
    isTableNoData,
    tableDimensionsData,
    tableTotal,
    tableData,
    tableSelection,
    defaultSelectionGames,
    transTableDataForSearch,
    getTableSearchList,
    handleClearSearch,
    onTableSortChage,
    onTablePagingChange,
    onTableCheckGame,
    onPageInit,
  } = useStoreRankMoreCore({
    entityType: CONSOLE_ENTITY_TYPE,
    pageLogCfg,
    primaryKey: PRIMARY_KEY,
    platformTopMetrics: CONSOLE_SOURCES_METRICS_KEYS,
    initialFetchParams,
    getIsSectional: (fetchParams: IStoreRankMoreCoreFetchParams) => {
      const topChartsName = fetchParams?.topchart_name;
      const granularity = fetchParams?.granularity;
      // nintendo平台 无 sectionanl模式
      if (topChartsName === CONSOLE_PLATFORM_NINTENDO) return false;
      // playstation平台的月粒度榜单 无sectional模式
      if (topChartsName === CONSOLE_PLATFORM_PLAYSTATION && granularity === 'monthly') return false;
      // 其余情况，regions为多选时，即sectional模式
      return fetchParams?.regions?.length > 1;
    },
  });

  // eslint-disable-next-line arrow-body-style
  const isMonthlyRank = useMemo(() => {
    return getIsMonthlyRank(platform, fetchParams?.granularity);
  }, [platform, fetchParams?.granularity]);

  const dateTypes = useMemo<IDateType[]>(() => {
    const topChartsName = fetchParams?.topchart_name;
    if (CONSOLE_SOURCES_PLAYSTATION_DAILY_MONTHLY_RANK.includes(topChartsName))
      return ['date', 'month'];
    if (CONSOLE_SOURCES_PLAYSTATION_MONTHLY_RANK.includes(topChartsName)) return ['month'];
    return ['date'];
  }, [fetchParams?.topchart_name]);

  const getExtendParams = () => {
    return handleFilterParams(filterExtend);
  };

  const handleFilterParams = (payload: any) => {
    const defaultFetchParams = {
      ...DEFAULT_FETCH_PARAMS,
      topchart_name: initialTopChartsName,
    };
    if (!isOperate) return defaultFetchParams;

    const filterExtendTmp = handleRangeDate2SingleDateRangeTimeFilterExtend(payload);
    const timeExtend = filterExtendTmp?.[FILTER_KEYS.RANGE_TIME];
    const composedCountryExtend = filterExtendTmp?.[FILTER_KEYS.COMPOSED_COUNTRY_SELECT];
    const singleAreaExtend = filterExtendTmp?.[FILTER_KEYS.SINGLE_AREA_SELECT];
    const initialGranularity = timeExtend?.granularity || DEFAULT_FETCH_PARAMS.granularity;

    const initialIsMonthlyRank = getIsMonthlyRank(initialPlatform, initialGranularity);

    const initialDate = getInitialDate(initialIsMonthlyRank, {
      extendStartDate: timeExtend?.start_date,
      extendEndDate: timeExtend?.end_date,
      defaultFilter: DEFAULT_FETCH_PARAMS,
    });
    const countriesValue = composedCountryExtend?.value?.countries;
    const isNintendoSingleRegion =
      initialPlatform === CONSOLE_PLATFORM_NINTENDO &&
      composedCountryExtend?.value?.type !== 'multiple';
    const nintendoUsOnly =
      initialPlatform === CONSOLE_PLATFORM_NINTENDO &&
      initialTopChartsName !== 'nintendo best sellers';

    const nitendoCode = nintendoUsOnly ? nitendoUSCountryCodes : nitendoCountryCodes;
    const nitendoCountries =
      countriesValue?.length === 1 && nitendoCode?.includes(countriesValue[0])
        ? countriesValue
        : ['us'];
    const initialRegions = getInitialRegions(initialIsMonthlyRank, {
      composedCountryExtend:
        isNintendoSingleRegion
          ? { countries: nitendoCountries }
          : composedCountryExtend?.value,
      singleAreaExtend: singleAreaExtend?.value,
      defaultFilter: DEFAULT_FETCH_PARAMS,
    });
    /** nintendo时修改继承值为过滤后的值 */
    if (isNintendoSingleRegion) {
      const newfilterExtend = {
        ...payload,
        [FILTER_KEYS.COMPOSED_COUNTRY_SELECT]: {
          value: { type: 'single', countries: nitendoCountries },
        },
      };
      setFilterExtend(newfilterExtend);
    }
    return {
      ...defaultFetchParams,
      regions: initialRegions,
      granularity: initialGranularity,
      start_date: initialDate,
      end_date: initialDate,
    };
  };

  useEffect(() => {
    if (isLoading) return;
    const countriesTmp = getAllCountries(regionsOptions);
    const multipleAllMarkets =
      countriesTmp?.length > 0 ? countriesTmp : DEFAULT_FETCH_PARAMS.regions;
    // 第一次进来，即没有继承还不用筛选器保存的情况，则默认为多国家全选，参数需要拿到国家全量值请求参数
    let newFetchParams = { ...fetchParams, regions: multipleAllMarkets };
    if (isOperate) {
      // 获取继承参数
      const extendParams = getExtendParams();
      newFetchParams = { ...newFetchParams, ...extendParams };
    }
    if (initialCheckAllCountries) {
      newFetchParams = { ...newFetchParams, regions: multipleAllMarkets };
    }
    // 添加会报错的参数组合，不调取接口
    const noNeedInit =
      newFetchParams?.order_metric === 'rank' && newFetchParams?.order_source !== '';
    if (!noNeedInit) {
      onPageInit(newFetchParams);
    }
  }, [isLoading, isOperate, initialTopChartsName, regionsOptions]);

  const nintendoUsOnly = useMemo(() => {
    return ['nintendo new release', 'nintendo coming soon'].includes(initialTopChartsName);
  }, [initialTopChartsName]);

  const downloadParams = useMemo(() => {
    return {
      ...fetchParams,
      downlaod_date_method: 'aggregated',
      page_size: 5000,
    };
  }, [fetchParams]);

  const downloadFileName = useMemo(() => {
    const startDate = fetchParams?.start_date || '';
    const endDate = fetchParams?.end_date || '';
    return `Store Ranks_Console_Region_Detail_${startDate}_${endDate}`;
  }, [fetchParams?.start_date, fetchParams?.end_date]);

  return (
    <>
      <MarqueeBanner />
      <StoreRankMoreCore
        entityType={CONSOLE_ENTITY_TYPE}
      searchType={SEARCH_TYPE}
      pageLogInfo={pageLogCfg}
      title={`${platformName} ${topName}`}
      isOperate={isOperate}
      isSectional={isSectional}
      isShowVisualis={true}
      fetchParams={fetchParams}
      visualisOptions={{
        isVisualisLoading,
        isVisualisNoData,
        seriesData,
        xAxisData,
        sortKeyName,
        currentLine,
        segments,
        segment,
        setSegment,
        yAxisFormatter,
        tooltipFormatter,
        createLegendsCustomMade,
      }}
      tablesOptions={{
        isTableLoading,
        isTableNoData,
        tableTotal,
        // 表格数据的主键，表格的recordKey、以及分段排名rank模式时，可视化需要用到
        primaryKey: PRIMARY_KEY,
        uunifiedKey: CONSOLE_UNIFIED_KEY,
        imageKey: 'cover',
        sectionalRankKeys: PC_CONSOLE_SECTIONAL_RANK_KEYS,
        sectionalRankInfos: PC_CONSOLE_SECTIONAL_RANK_INFOS,
        platformTopMetrics: CONSOLE_SOURCES_METRICS_KEYS,
        platformTopInfos: CONSOLE_PLATFORM_TOP_INFOS,
        tableDimensionsData,
        tableData,
        tableSelection,
        defaultSelectionGames,
        transTableDataForSearch,
        getTableSearchList,
        handleClearSearch,
        onTableSortChage,
        onTablePagingChange,
        onTableCheckGame,
      }}
      filter={
        <Filter
          isOperate={isOperate}
          initialCheckAllCountries={initialCheckAllCountries}
          isMonthlyRank={isMonthlyRank}
          getIsMonthlyRank={(payload: Partial<IStoreRankMoreCoreFetchParams>) =>
            getIsMonthlyRank(platform, payload?.granularity)
          }
          isRegionVisible={true} // !nintendoUsOnly}
          isRegionDisabled={false} // nintendoUsOnly}
          regionOptions={nintendoUsOnly ? regionsOptionsMap.nintendoUsOnly : regionsOptions}
          typeList={dateTypes}
          granularity={fetchParams?.granularity}
          startDate={fetchParams?.start_date}
          endDate={fetchParams?.end_date}
          regions={fetchParams?.regions}
          onInit={handleInit}
          onConfirm={handleConfirm}
          onReset={handleReset}
          isNitendo={initialPlatform === CONSOLE_PLATFORM_NINTENDO}
        />
      }
      exportTable={
        <ExportTable
          entityType={CONSOLE_ENTITY_TYPE}
          // @ts-ignore
          businessType={BUSINESS_TYPE}
          downloadLogConfigKey={downloadExcelLogCfgKey}
          isDisableDownload={isTableLoading}
          isNeedCheckedLines={!isSectional}
          downloadModalSize={locale === LANGUAGE_CN ? 580 : 686}
          tableParams={downloadParams}
          total={tableTotal}
          getCheckedLinesCfg={() => ({
            key: 'search_ids',
            value: isSectional
              ? []
              : tableSelection?.map((item) => item?.app_id) || [],
          })}
          newStyleFileName={downloadFileName}
          isShowMaxDownloadRowsTips={false}
          useDownloadAmountType={true}
          useWebDownload={true}
          downloadKey={'consoleStoreRankMore'}
        />
      }
    />
    </>
  );
};

export default ConsoleStoreRankMore;
